import { AppProvider } from "@shopify/polaris";
/**
 * The --p-* TOKEN LAYER ONLY — not Polaris React's monolithic stylesheet.
 *
 * WHY: the full sheet is 447,686 B raw / 53,288 B gz and it is RENDER-BLOCKING, loaded
 * so that three components can draw — SkeletonBodyText, SkeletonDisplayText and
 * ProgressBar. Their actual rules total 5,515 B / 1,101 B gz. We were paying ~48x.
 * The component layer now loads async via ./utils/polarisCss.js.
 *
 * VERIFIED byte-equivalent, not assumed: diffing the --p-* declaration sets of
 * @shopify/polaris-tokens/css/styles.css against the built blocking CSS gives
 * 452 tokens on both sides, tokens-only [] and built-only []. 28,240 B raw / 4,350 B gz.
 *
 * ⚠️ THIS ONE STAYS RENDER-BLOCKING ON PURPOSE. AppProvider.componentDidMount writes
 * `document.body.style.backgroundColor = 'var(--p-color-bg)'` INLINE
 * (@shopify/polaris/build/esm/components/AppProvider/AppProvider.js:34-36). With no
 * token layer that var is invalid at computed-value time, so it computes to
 * `transparent` — and an inline declaration beats any stylesheet, so you cannot patch
 * it from CSS. Plus there are 394 `var(--p-…)` sites under web/. Deferring this half
 * is a full-page colour flash.
 *
 * ⚠️ The specifier resolves through the package's `exports` map
 * ("./css/*": "./dist/css/*") to dist/css/styles.css. Checking the literal path
 * node_modules/@shopify/polaris-tokens/css/styles.css on disk FAILS — it is not there.
 * The import is still correct. Keep the version pinned in lockstep with @shopify/polaris.
 */
import "@shopify/polaris-tokens/css/styles.css";
import enTranslations from "@shopify/polaris/locales/en.json";
import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
// Direct import to avoid barrel export - prevents pulling Preview (2.6MB) and other heavy components
import App from "./components/App";
import { adoptBootLoading } from "./utils/adminLoading";
import { markBoot } from "./utils/bootTiming";

/**
 * `./styles/drag-drop.css` is NOT imported here any more (moved 2026-08-16).
 *
 * It was 10,217 B — roughly 30% of the RENDER-BLOCKING entry stylesheet — on every
 * route, while every selector in it is visual-editor-only. It now lives in
 * web/components/Preview/PreviewContainer.jsx, so it rides the EXISTING Preview-*.css
 * asset (no new HTTP request; importing it from PopupSettings would have created one,
 * since that chunk ships no CSS today).
 *
 * ⚠️ Do not move it back, and do not "tidy" it into PopupSettings. See the cross-chunk
 * invariant documented at the import site in PreviewContainer.jsx.
 */

/**
 * FIRST boot mark of this document. `.startTime` is the ms from navigation start to the
 * moment the entry chunk finished evaluating, i.e. everything the HTML cost us: the two
 * blocking <head> scripts, vendor-react, vendor-polaris, vendor-utils and this chunk.
 *
 * ⚠ It is a MARK, not a console.log, deliberately — vite.config.mjs strips console.* in
 * production, so performance marks are the only instrumentation that survives a real build.
 *
 * ⚠ It sits AFTER the import block on purpose. Imports hoist, so no statement can run
 * before them; this measures "entry chunk evaluated", not "entry chunk fetched". That is
 * the number we want anyway — it is when our code first gets control.
 *
 * The editor opens in its own App Bridge Modal iframe, a separate JS realm with its own
 * module cache, so this runs afresh for the editor and its value is the editor's own boot
 * cost — not the dashboard's. That is what makes measureEditorBoot() meaningful.
 */
markBoot("boot_entry");

/**
 * Take ownership of the admin header loading bar from the inline script in index.html.
 *
 * That script turned the bar on at the top of the body — the earliest point at which
 * window.shopify exists — to cover the seconds when the iframe is genuinely blank. This is
 * the first line of OUR code that runs, so it is the right place to adopt the claim: from
 * here the module's refcount owns it, every boot gate (BootShell, RouteLoader) holds it via
 * useAdminLoading(), and the inline script's 20 s fail-safe stands down.
 *
 * ⚠ Not inside a component and not in an effect, on purpose. React's first render is still
 * a chunk-load away at this point; waiting for it would leave the claim unowned across
 * exactly the window this is meant to cover. It is a no-op when the flag is unset (outside
 * the embedded admin, or if App Bridge never initialised), so it is safe to run eagerly.
 */
adoptBootLoading();

// Console.log deactivation for production
// Secure debug mode detection with environment awareness
const isDebugForced = (() => {
  // In production, never allow debug mode
  if (import.meta.env.PROD) {
    return false;
  }
  
  // In development, check multiple sources
  try {
    // 1. Check localStorage (persists across sessions)
    const storageDebug = localStorage.getItem('nudgesmart_debug');
    if (storageDebug === 'true') return true;
    
    // 2. Check session storage (current session only)
    const sessionDebug = sessionStorage.getItem('nudgesmart_debug');
    if (sessionDebug === 'true') return true;
    
    // 3. Only in dev: check URL parameter for temporary debugging
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('nudgesmart_debug') === 'true') {
      // Auto-save to session for this session
      sessionStorage.setItem('nudgesmart_debug', 'true');
      return true;
    }
    
    return false;
  } catch (e) {
    // If storage access fails, default to false
    console.warn('[Nudgesmart] Could not access storage for debug mode:', e);
    return false;
  }
})();

// Secure console management with environment awareness
(() => {
  // Only modify console in production
  if (import.meta.env.PROD) {
    // Store originals in closure (not globally accessible)
    const originalConsole = {
      log: console.log,
      warn: console.warn,
      error: console.error,
      info: console.info,
      debug: console.debug,
      trace: console.trace,
      table: console.table,
      group: console.group,
      groupEnd: console.groupEnd
    };
    
    // Create no-op functions
    const noop = () => {};
    
    // Override console methods
    console.log = noop;
    console.info = noop;
    console.debug = noop;
    console.trace = noop;
    console.table = noop;
    console.group = noop;
    console.groupEnd = noop;
    
    // Keep warn for important warnings (with filtering)
    console.warn = (...args) => {
      // Only log warnings that start with [Nudgesmart]
      if (args[0]?.toString().startsWith('[Nudgesmart]')) {
        originalConsole.warn(...args);
      }
    };
    
    // Keep error but sanitize sensitive data
    console.error = (...args) => {
      // Always log errors but sanitize
      const sanitizedArgs = args.map(arg => {
        if (typeof arg === 'string') {
          // Remove potential sensitive data
          return arg
            .replace(/[\w\.-]+@[\w\.-]+\.\w+/g, '[EMAIL]')
            .replace(/\b[A-Za-z0-9]{32,}\b/g, '[KEY]');
        }
        return arg;
      });
      originalConsole.error('[PRODUCTION]', ...sanitizedArgs);
    };
    
    // No global debug functions in production
    // window.restoreConsole NOT defined
    // window.isConsoleDisabled NOT defined
    
  } else {
    // Development environment - add helpful debug utilities
    window.__nudgesmart_dev = {
      version: '1.0.0',
      env: import.meta.env.MODE,
      debug: {
        enableConsole: () => {
          localStorage.setItem('nudgesmart_debug', 'true');
          console.log('[Nudgesmart Dev] Debug mode enabled');
        },
        disableConsole: () => {
          localStorage.setItem('nudgesmart_debug', 'false');
          console.log('[Nudgesmart Dev] Debug mode disabled');
        },
        status: () => ({
          debugMode: isDebugForced,
          environment: import.meta.env.MODE,
          timestamp: new Date().toISOString()
        })
      }
    };
    
    console.log(
      '%c[Nudgesmart Dev] Development mode active',
      'color: #00dc82; font-weight: bold;',
      '\nDebug utilities available at window.__nudgesmart_dev'
    );
  }
})();

/**
 * js-confetti is NOT initialised here any more (removed 2026-08-16).
 *
 * It was imported at module scope purely to set window.JSConfetti, which put the whole
 * library on the eager critical path. web/components/Preview/hooks/useSpinWheelPreview.js:8-15
 * already does the identical thing inside the LAZY Preview chunk — where the only real
 * consumer lives — and does more:
 *
 *   if (typeof window !== 'undefined' && !window.JSConfetti) {
 *     window.JSConfetti = JSConfetti;
 *     if (window.NudgesmartComponentCore?.initializeConfetti) {
 *       window.NudgesmartComponentCore.initializeConfetti();   // <-- never ran before
 *     }
 *   }
 *
 * Because this file set the global FIRST, that `!window.JSConfetti` guard was always
 * false, so initializeConfetti() had never executed in this app. Deleting the eager copy
 * makes it run. Behaviour is otherwise unchanged: component-core's showLuckyWheelConfetti
 * re-reads the global lazily at call time, so nothing depends on it existing at boot.
 *
 * WHO ACTUALLY READS window.JSConfetti — checked exhaustively before removing this:
 *
 *   • VisualEditorV2/onboarding/OnboardingTour.jsx:17 — imports `canvas-confetti`, a
 *     DIFFERENT package, directly. Never touches this global. Unaffected.
 *
 *   • components/utils/nudgesmart-component-core.js  (initializeConfetti)
 *     components/utils/nudgesmart-spinwheel-core.js  (:38, :599, :784)
 *     Both DO read window.JSConfetti — but only as a THIRD-choice fallback.
 *     initializeConfetti() tries, in order:
 *         typeof es_default !== "undefined"   ← js-confetti is BUNDLED INTO component-core
 *         typeof JSConfetti  !== "undefined"
 *         window.JSConfetti                   ← this global
 *     so the bundled copy wins and the global was already redundant for them.
 *     (Both files are BUILD OUTPUT synced from extensions/ — never edit them here.)
 *
 *   • Every consumer of those two modules lives under web/components/Preview/**, i.e.
 *     inside the LAZY Preview chunk — the same chunk where useSpinWheelPreview.js sets
 *     the global. Imports hoist, so component-core's module body evaluates before line 9
 *     runs; but initializeConfetti() is a METHOD called at spin time, long after. There
 *     is no path that needs this global before the Preview chunk has loaded.
 */

const root = document.getElementById("root");
if (!root) throw new Error("#root element not found for booting react app");

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <AppProvider i18n={enTranslations}>
      <App />
    </AppProvider>
  </React.StrictMode>
);
